import * as React from 'react'
import { Search, X, ChevronDown, SlidersHorizontal, Calendar, Check, CircleDot, Download } from 'lucide-react'
import type { Table } from '@tanstack/react-table'
import type { PackageItemType, PackageView } from '@/lib/api-client'

export type PackageDatePreset = 'all' | '18months' | '12months' | 'custom'

const DATE_PRESET_OPTIONS: { value: PackageDatePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: '18months', label: 'Last 18 Months' },
  { value: '12months', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom...' },
]

const ITEM_TYPE_OPTIONS: { value: PackageItemType; label: string }[] = [
  { value: 'treatment', label: 'Treatment' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'foc', label: 'FOC (bonus)' },
]

const COLUMN_LABELS: Record<string, string> = {
  purchaseDate: 'Purchase',
  invoiceNo: 'Invoice',
  packageName: 'Package',
  itemName: 'Item',
  itemType: 'Type',
  boughtQty: 'Bought',
  usedQty: 'Used',
  balanceQty: 'Balance',
  packagePrice: 'Pkg Price',
  branchName: 'Branch',
}

interface PackageToolbarProps<TData> {
  search: string
  onSearchChange: (v: string) => void
  datePreset: PackageDatePreset
  onDatePresetChange: (v: PackageDatePreset) => void
  customFrom: string
  customTo: string
  onCustomFromChange: (v: string) => void
  onCustomToChange: (v: string) => void
  itemTypes: PackageItemType[]
  onItemTypesChange: (v: PackageItemType[]) => void
  outstandingOnly: boolean
  onOutstandingOnlyChange: (v: boolean) => void
  view: PackageView
  onViewChange: (v: PackageView) => void
  onExport: () => void
  table: Table<TData>
}

function FilterPill({ open, onToggle, icon, label, width, children }: {
  open: boolean; onToggle: () => void; icon?: React.ReactNode; label: React.ReactNode; width: string; children: React.ReactNode
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 ${width} border border-border transition-all hover:bg-hover`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="text-[13px] font-normal text-text-dark truncate">{label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onToggle} />
          <div className="absolute right-0 top-full mt-1 min-w-[200px] rounded-lg border border-border bg-card p-1 shadow-lg z-50 max-h-[400px] overflow-y-auto">
            {children}
          </div>
        </>
      )}
    </div>
  )
}

export function PackageToolbar<TData>({
  search, onSearchChange,
  datePreset, onDatePresetChange, customFrom, customTo, onCustomFromChange, onCustomToChange,
  itemTypes, onItemTypesChange,
  outstandingOnly, onOutstandingOnlyChange,
  view, onViewChange,
  onExport,
  table,
}: PackageToolbarProps<TData>) {
  const [openPanel, setOpenPanel] = React.useState<string | null>(null)
  const togglePanel = (k: string) => setOpenPanel(openPanel === k ? null : k)

  const dateLabel = DATE_PRESET_OPTIONS.find((o) => o.value === datePreset)?.label ?? 'All Time'
  const typeLabel =
    itemTypes.length === 0 || itemTypes.length === ITEM_TYPE_OPTIONS.length
      ? 'All Types'
      : itemTypes.length === 1
        ? ITEM_TYPE_OPTIONS.find((t) => t.value === itemTypes[0])?.label
        : `${itemTypes.length} types`

  const toggleType = (t: PackageItemType) => {
    onItemTypesChange(itemTypes.includes(t) ? itemTypes.filter((x) => x !== t) : [...itemTypes, t])
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Top row: search + view toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[280px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
          <Search className="w-4 h-4 text-foreground flex-shrink-0" />
          <input
            type="text"
            placeholder="Search patient, PNO, invoice, package..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-none outline-none text-[13px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
          />
          {search && (
            <button onClick={() => onSearchChange('')} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover">
              <X className="w-3.5 h-3.5 text-text-muted" />
            </button>
          )}
        </div>

        <div className="inline-flex rounded-full border border-border bg-card p-0.5 self-start sm:self-auto">
          <button
            onClick={() => onViewChange('patients')}
            className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${view === 'patients' ? 'bg-info text-white' : 'text-text-muted hover:text-text-dark'}`}
          >
            Call List
          </button>
          <button
            onClick={() => onViewChange('rows')}
            className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors ${view === 'rows' ? 'bg-info text-white' : 'text-text-muted hover:text-text-dark'}`}
          >
            Item Rows
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2.5">
        {/* Date preset */}
        <FilterPill
          open={openPanel === 'date'}
          onToggle={() => togglePanel('date')}
          icon={<Calendar className="w-4 h-4 text-foreground flex-shrink-0" />}
          label={dateLabel}
          width="w-[160px]"
        >
          {DATE_PRESET_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onDatePresetChange(opt.value)
                if (opt.value !== 'custom') setOpenPanel(null)
              }}
              className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${datePreset === opt.value ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'}`}
            >
              {opt.label}
              {datePreset === opt.value && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
          {datePreset === 'custom' && (
            <div className="border-t border-border mt-1 pt-2 px-2 pb-1 space-y-2">
              <div>
                <label className="text-[10px] uppercase text-text-muted">From</label>
                <input type="date" value={customFrom} onChange={(e) => onCustomFromChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-text-muted">To</label>
                <input type="date" value={customTo} onChange={(e) => onCustomToChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
              </div>
            </div>
          )}
        </FilterPill>

        {/* Item type multi-select */}
        <FilterPill open={openPanel === 'type'} onToggle={() => togglePanel('type')} label={typeLabel} width="w-[140px]">
          {ITEM_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggleType(opt.value)}
              className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] text-text-dark hover:bg-hover transition-colors"
            >
              {opt.label}
              {itemTypes.includes(opt.value) && <Check className="w-3.5 h-3.5 text-success" />}
            </button>
          ))}
          {itemTypes.length > 0 && (
            <button onClick={() => onItemTypesChange([])} className="flex w-full items-center justify-center rounded-md px-2.5 py-1.5 text-[12px] text-text-muted hover:bg-hover border-t border-border mt-1">
              Show all
            </button>
          )}
        </FilterPill>

        {/* Outstanding-only toggle */}
        <button
          onClick={() => onOutstandingOnlyChange(!outstandingOnly)}
          className={`flex items-center gap-2 rounded-full px-3.5 py-2 border text-[12px] transition-all ${outstandingOnly ? 'bg-info/10 text-info border-info/30' : 'bg-card text-text-muted border-border hover:bg-hover'}`}
        >
          <CircleDot className="w-3.5 h-3.5" />
          {outstandingOnly ? 'Outstanding only' : 'All (incl. done)'}
        </button>

        {/* Column visibility (rows view only) */}
        {view === 'rows' && (
          <FilterPill
            open={openPanel === 'columns'}
            onToggle={() => togglePanel('columns')}
            icon={<SlidersHorizontal className="w-4 h-4 text-foreground flex-shrink-0" />}
            label="Columns"
            width="w-[120px]"
          >
            <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">Toggle columns</div>
            {table.getAllColumns().filter((col) => col.getCanHide()).map((col) => (
              <button
                key={col.id}
                onClick={() => col.toggleVisibility(!col.getIsVisible())}
                className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${col.getIsVisible() ? 'text-text-dark' : 'text-text-muted'} hover:bg-hover`}
              >
                {COLUMN_LABELS[col.id] || col.id}
                {col.getIsVisible() && <Check className="w-3.5 h-3.5 text-success" />}
              </button>
            ))}
          </FilterPill>
        )}

        {/* Export call-list */}
        <button
          onClick={onExport}
          className="flex items-center gap-2 rounded-full px-3.5 py-2 border border-border bg-card text-[12px] text-text-dark hover:bg-hover transition-all ml-auto"
        >
          <Download className="w-3.5 h-3.5" />
          Export call list
        </button>
      </div>
    </div>
  )
}
